/**
 * Small LRU of rendered page bitmaps keyed by (page, pixel ratio), so scrolling back
 * to a page that left the virtualization window repaints it without a pdf.js render.
 * Bounded both by entry count and by total pixels to keep memory predictable.
 */
export class RenderCache<T extends { width: number; height: number }> {
	private entries = new Map<string, T>();

	constructor(
		private readonly maxEntries = 6,
		private readonly maxPixels = 2 ** 25,
		/** Called with each entry dropped by eviction or `clear()`. */
		private readonly onEvict?: (value: T) => void,
	) {}

	static key(pageIndex: number, ratio: number) {
		return `${pageIndex}@${ratio.toFixed(3)}`;
	}

	get(key: string): T | undefined {
		const value = this.entries.get(key);
		if (value) {
			// Re-insert to mark as most recently used.
			this.entries.delete(key);
			this.entries.set(key, value);
		}
		return value;
	}

	set(key: string, value: T) {
		this.entries.delete(key);
		this.entries.set(key, value);
		this.evict();
	}

	/** Whether `value` is one of the cached entries. */
	holds(value: T) {
		let isHeld = false;
		this.entries.forEach((entry) => {
			if (entry === value) isHeld = true;
		});
		return isHeld;
	}

	clear() {
		const values = Array.from(this.entries.values());
		this.entries.clear();
		values.forEach((value) => this.onEvict?.(value));
	}

	get size() {
		return this.entries.size;
	}

	private totalPixels() {
		let total = 0;
		this.entries.forEach((entry) => {
			total += entry.width * entry.height;
		});
		return total;
	}

	private evict() {
		while (
			this.entries.size > this.maxEntries ||
			(this.entries.size > 1 && this.totalPixels() > this.maxPixels)
		) {
			const oldest = this.entries.keys().next().value;
			if (oldest === undefined) break;
			const value = this.entries.get(oldest);
			this.entries.delete(oldest);
			if (value) this.onEvict?.(value);
		}
	}
}

/**
 * Drops a canvas's backing store if it is no longer on screen. Safari keeps the
 * memory of detached canvases until garbage collection and counts it against the
 * page's canvas limit, so zeroing the size frees it right away.
 */
export const releaseCanvas = (canvas: HTMLCanvasElement) => {
	if (canvas.isConnected) return;
	canvas.width = 0;
	canvas.height = 0;
};
